import { User } from './user.interface';

export interface IFile {
    id: string;
    originalName: string;
    contentLength: number;
    contentType: string;
    s3Key: string;
    directory: string;
    parentId: string;
    version: string;
    tenancy: string;
    name: string;
    size: number;
    retryCount: number;
    nodeType: string;
    status: string;
    lastModified: Date;
    updatedBy: User;
    createdAt: Date;
    createdBy: User;
    deletedAt: Date;
}

export interface FileResponse {
    content: IFile[];
    pagination: any;
}

export interface FileRequest {
    id?: string;
    originalName: string;
    directory: string;
    contentType?: string;
}
interface FileTypeMetadata {
    icon: string;
    color: string;
    label: string;
}

const FILE_TYPES: Record<string, FileTypeMetadata> = {
    // PDF
    'application/pdf': {
        icon: 'picture_as_pdf',
        color: 'text-red-500',
        label: 'PDF'
    },

    // Word
    'application/msword': {
        icon: 'description',
        color: 'text-blue-500',
        label: 'Word'
    },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
        icon: 'description',
        color: 'text-blue-500',
        label: 'Word'
    },

    // Excel
    'application/vnd.ms-excel': {
        icon: 'table_chart',
        color: 'text-green-500',
        label: 'Excel'
    },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
        icon: 'table_chart',
        color: 'text-green-500',
        label: 'Excel'
    },
    'text/csv': {
        icon: 'table_chart',
        color: 'text-green-500',
        label: 'CSV'
    },

    // PowerPoint
    'application/vnd.ms-powerpoint': {
        icon: 'slideshow',
        color: 'text-orange-500',
        label: 'PowerPoint'
    },
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
        icon: 'slideshow',
        color: 'text-orange-500',
        label: 'PowerPoint'
    },

    // Compactados
    'application/zip': {
        icon: 'archive',
        color: 'text-amber-500',
        label: 'ZIP'
    },
    'application/x-zip-compressed': {
        icon: 'archive',
        color: 'text-amber-500',
        label: 'ZIP'
    },
    'application/x-rar-compressed': {
        icon: 'archive',
        color: 'text-amber-500',
        label: 'RAR'
    },
    'application/x-7z-compressed': {
        icon: 'archive',
        color: 'text-amber-500',
        label: '7-Zip'
    },
    'application/x-tar': {
        icon: 'archive',
        color: 'text-amber-500',
        label: 'TAR'
    },

    // Texto
    'text/plain': {
        icon: 'text_snippet',
        color: 'text-gray-500',
        label: 'TXT'
    },

    // Código
    'application/json': {
        icon: 'code',
        color: 'text-cyan-500',
        label: 'JSON'
    },
    'application/xml': {
        icon: 'code',
        color: 'text-cyan-500',
        label: 'XML'
    },
    'text/html': {
        icon: 'code',
        color: 'text-cyan-500',
        label: 'HTML'
    },
    'text/css': {
        icon: 'code',
        color: 'text-cyan-500',
        label: 'CSS'
    },
    'application/javascript': {
        icon: 'code',
        color: 'text-cyan-500',
        label: 'JavaScript'
    }
};

function getFileMetadata(
    contentType?: string,
    isDir = false
): FileTypeMetadata {

    if (isDir) {
        return {
            icon: 'folder',
            color: 'text-yellow-500',
            label: 'Pasta'
        };
    }

    if (!contentType) {
        return {
            icon: 'insert_drive_file',
            color: 'text-gray-500',
            label: 'Arquivo'
        };
    }

    const type = contentType.toLowerCase();

    if (type.startsWith('image/')) {
        return {
            icon: 'image',
            color: 'text-purple-500',
            label: 'Imagem'
        };
    }

    if (type.startsWith('video/')) {
        return {
            icon: 'movie',
            color: 'text-indigo-500',
            label: 'Vídeo'
        };
    }

    if (type.startsWith('audio/')) {
        return {
            icon: 'audiotrack',
            color: 'text-teal-500',
            label: 'Áudio'
        };
    }

    return (
        FILE_TYPES[type] ?? {
            icon: 'insert_drive_file',
            color: 'text-gray-500',
            label: 'Arquivo'
        }
    );
}

export function getFileIcon(
    contentType?: string,
    isDir = false
): string {
    return getFileMetadata(contentType, isDir).icon;
}

export function getFileTypeColor(
    contentType?: string,
    isDir = false
): string {
    return getFileMetadata(contentType, isDir).color;
}

export function getFileTypeLabel(
    contentType?: string,
    isDir = false
): string {
    return getFileMetadata(contentType, isDir).label;
}